import React, { useState } from 'react';
import { ArrowLeft, Plus, CheckCircle2, AlertTriangle, Wrench, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const CheckInEquipment = ({ onBack, equipmentName = "Sony FX6 Cinema Camera" }) => {
  const [checklistItems, setChecklistItems] = useState([
    { id: 1, text: "Equipment is clean", checked: false },
    { id: 2, text: "No visible damage", checked: false },
    { id: 3, text: "Batteries charged/included", checked: false },
    { id: 4, text: "Memory cards removed", checked: false },
    { id: 5, text: "Equipment tested and functional", checked: false },
    { id: 6, text: "Log equipment status for tracking", checked: false },
  ]);
  const [newItemText, setNewItemText] = useState("");

  const handleAddChecklistItem = () => {
    if (newItemText.trim()) {
      setChecklistItems([
        ...checklistItems,
        { id: Date.now(), text: newItemText, checked: false }
      ]);
      setNewItemText("");
    }
  };

  const toggleChecklistItem = (id) => {
    setChecklistItems(checklistItems.map(item =>
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  // Mock Issues Data
  const issues = [
    { id: 1, name: 'Battery Grip', priority: 'low', label: 'low' },
    { id: 2, name: 'High-Speed SD Card', priority: 'low', label: 'low' },
    { id: 3, name: 'Tripod Stand', priority: 'medium', label: 'medium' },
    { id: 4, name: 'External Microphone', priority: 'medium', label: 'medium' },
    { id: 5, name: 'Lens Cleaner Kit', priority: 'high', label: 'high' },
    { id: 6, name: 'Camera Bag', priority: 'low', label: 'low' },
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-orange-600 bg-orange-50';
      case 'low': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Back Button */}
      <Button
        variant="ghost"
        className="mb-6 pl-0 hover:pl-2 transition-all gap-2 text-gray-600 hover:bg-transparent"
        onClick={onBack}
      >
        <ArrowLeft size={20} />
        Back to Equipment Management
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Forms */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h1 className="text-2xl font-bold">Check In Equipment</h1>
            <p className="text-gray-500 mt-1">Return Canon EOS R5 to inventory</p>
          </div>

          {/* Equipment Inspection */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Equipment Inspection</h2>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Equipment Condition</label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select condition" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="excellent">Excellent</SelectItem>
                    <SelectItem value="good">Good</SelectItem>
                    <SelectItem value="fair">Fair</SelectItem>
                    <SelectItem value="poor">Poor</SelectItem>
                    <SelectItem value="damaged">Damaged</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Inspection Notes</label>
                <Textarea
                  placeholder="Document any issues, damage, or maintenance needed..."
                  className="min-h-[100px] resize-none"
                />
              </div>
            </div>
          </div>

          {/* Return Checklist */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Return Checklist</h2>

            <div className="flex gap-3 mb-6">
              <Input
                placeholder="Add checklist item..."
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
              />
              <Button
                className="bg-button-coloured text-white min-w-[80px]"
                onClick={handleAddChecklistItem}
              >
                + Add
              </Button>
            </div>

            <div className="space-y-3">
              {checklistItems.map((item) => (
                <div key={item.id} className="flex items-center space-x-3 p-3 border border-gray-100 rounded-lg bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <Checkbox
                    id={`item-${item.id}`}
                    checked={item.checked}
                    onCheckedChange={() => toggleChecklistItem(item.id)}
                    className="border-gray-300 data-[state=checked]:bg-button-coloured data-[state=checked]:border-button-coloured"
                  />
                  <label
                    htmlFor={`item-${item.id}`}
                    className="text-sm text-gray-700 font-medium cursor-pointer select-none flex-1"
                  >
                    {item.text}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Issues Reported */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Issues Reported</h2>
            <p className="text-sm text-gray-500 mb-4">Mark any issues that need attention</p>

            <div className="space-y-2">
              {issues.map((issue) => (
                <div key={issue.id} className="flex items-center justify-between p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <span className="text-sm font-medium text-gray-700">{issue.name}</span>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full uppercase ${getPriorityColor(issue.priority)}`}>
                    {issue.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Button className="w-full bg-button-coloured text-white gap-2">
                <CheckCircle2 size={18} />
                Complete Check-In
              </Button>
              <Button
                variant="outline"
                className="w-full border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-700 gap-2"
              >
                <Wrench size={18} />
                Mark for Maintenance
              </Button>
              <Button
                variant="outline"
                className="w-full border-gray-200 text-gray-700 hover:bg-gray-50"
                onClick={onBack}
              >
                Cancel
              </Button>
            </div>
          </div>

          {/* Current Assignment */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Current Assignment</h2>

            <div className="flex gap-4 mb-6">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 font-semibold text-sm shrink-0">
                CN
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm leading-tight mb-1">{equipmentName}</h3>
                <p className="text-xs text-gray-500">Sony FX6 Cinema Camera Kit</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <span className="text-gray-500">Assigned To</span>
                  <span className="font-medium text-gray-900 text-right">Michael Chen</span>

                  <span className="text-gray-500">Project</span>
                  <span className="font-medium text-gray-900 text-right">Tech Conference 2024</span>

                  <span className="text-gray-500">Check-out Date</span>
                  <span className="font-medium text-gray-900 text-right">2024-01-14</span>

                  <span className="text-gray-500">Days Out</span>
                  <span className="font-medium text-gray-900 text-right">2 days</span>
                </div>
              </div>
            </div>
          </div>

          {/* Important Notes */}
          <div className="bg-red-50/50 border border-red-100 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
              <Info size={16} /> Important Notes
            </h3>
            <ul className="text-xs text-red-700 space-y-1 list-disc pl-4">
              <li>Inspect equipment carefully</li>
              <li>Document all issues</li>
              <li>Flag if maintenance needed</li>
            </ul>
          </div>

          {/* Check-In Process */}
          <div className="bg-green-50/50 border border-green-100 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
              <CheckCircle2 size={16} /> Check-In Process
            </h3>
            <p className="text-xs text-green-700 leading-relaxed">
              After check-in, equipment status will be updated and it will be available for future bookings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckInEquipment;

